import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { formatBookingReference } from '@/utils/bookingUtils';
import { usePaymentResult } from './PaymentResult/usePaymentResult';
import VerifyingState from './PaymentResult/VerifyingState';
import ErrorState from './PaymentResult/ErrorState';
import StatusHeader from './PaymentResult/StatusHeader';
import PaymentDetailsPanel from './PaymentResult/PaymentDetailsPanel';
import ResultActions from './PaymentResult/ResultActions';
import ResultFooterNote from './PaymentResult/ResultFooterNote';

export default function PaymentResult() {
  const navigate = useNavigate();
  const { bookingId, verifying, result, error, location, checkGatewayStatus } = usePaymentResult();

  if (verifying) {
    return <VerifyingState />;
  }

  if (error) {
    return (
      <ErrorState
        error={error}
        onViewBookings={() => navigate('/bookings')}
        onGoHome={() => navigate('/')}
      />
    );
  }

  if (!result) {
    return null;
  }

  const isSuccess = result.is_success;
  const isPending = result.payment_status === 'pending' || result.payment_status === 'processing';
  // Extracted from nested-ternary in JSX for readability + ESLint compliance.
  const bgGradient = isSuccess
    ? 'from-green-50 to-gray-100'
    : isPending
      ? 'from-yellow-50 to-gray-100'
      : 'from-red-50 to-gray-100';
  const statusColor = isSuccess
    ? 'text-green-600'
    : isPending
      ? 'text-yellow-600'
      : 'text-red-600';

  return (
    <div className={`min-h-screen bg-gradient-to-br ${bgGradient} flex items-center justify-center p-4`}>
      <Card className="w-full max-w-2xl p-8">
        <StatusHeader isSuccess={isSuccess} isPending={isPending} result={result} />

        <PaymentDetailsPanel result={result} location={location} statusColor={statusColor} />

        {/* Booking Reference for Customer Records */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            <span className="font-semibold">Booking Reference:</span> {formatBookingReference(bookingId)}
          </p>
          <p className="text-xs text-blue-600 mt-1">
            Please save this reference number for your records
          </p>
        </div>

        <ResultActions
          isSuccess={isSuccess}
          isPending={isPending}
          verifying={verifying}
          onCheckStatus={checkGatewayStatus}
          onViewBookings={() => navigate('/bookings')}
          onBookAnother={() => navigate('/book')}
          onTryAgain={() => navigate(`/payment/${bookingId}`)}
        />

        <ResultFooterNote isSuccess={isSuccess} isPending={isPending} />
      </Card>
    </div>
  );
}
